import { WORD_BLANK_LINE_PT } from '../../constants';

import type { DraftBlock, DraftParagraph } from './types';

export const draftParagraphsOf = (blocks: DraftBlock[]): DraftParagraph[] =>
  blocks.flatMap((block) => {
    if (block.kind === 'paragraph') return [block];
    const children = block.kind === 'table' ? block.rows.flat() : block.items;
    return children.flatMap((child) => draftParagraphsOf(child.blocks));
  });

export const mapBlockSequences = (blocks: DraftBlock[], map: (sequence: DraftBlock[]) => DraftBlock[]): DraftBlock[] =>
  map(
    blocks.map((block): DraftBlock => {
      switch (block.kind) {
        case 'paragraph':
          return block;
        case 'table':
          return {
            ...block,
            rows: block.rows.map((cells) =>
              cells.map((cell) => ({ ...cell, blocks: mapBlockSequences(cell.blocks, map) })),
            ),
          };
        case 'list':
          return {
            ...block,
            items: block.items.map((item) => ({ ...item, blocks: mapBlockSequences(item.blocks, map) })),
          };
      }
    }),
  );

export const mapDraftParagraphs = (
  blocks: DraftBlock[],
  map: (paragraph: DraftParagraph) => DraftParagraph,
): DraftBlock[] =>
  mapBlockSequences(blocks, (sequence) => sequence.map((block) => (block.kind === 'paragraph' ? map(block) : block)));

export const paragraphTextOf = ({ runs }: DraftParagraph) =>
  runs
    .map((run) => (run.kind === 'text' ? run.text : ' '))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

export const isBlankParagraph = (block: DraftBlock) => block.kind === 'paragraph' && !paragraphTextOf(block);

export const firstLineLeftOf = ({ indent: { left, hanging = 0 } }: DraftParagraph) =>
  left === undefined ? undefined : left - hanging;

export const withBlanksFolded = (paragraph: DraftParagraph, blanks: DraftParagraph[]): DraftParagraph =>
  paragraph.spaceAfter === undefined
    ? paragraph
    : {
        ...paragraph,
        spaceAfter: blanks.reduce(
          (sum, blank) => sum + WORD_BLANK_LINE_PT + (blank.spaceAfter ?? 0),
          paragraph.spaceAfter,
        ),
      };
